import React, { useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  FormOutlined,
  PrinterOutlined,
  PlusOutlined,
  SaveOutlined,
  SendOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReactDOM from 'react-dom/client';
import salesOrderService from '@/services/salesOrder.service';
import salesOrderTimesheetService, {
  type SalesOrderTimesheet,
  type SalesOrderTimesheetPerson,
} from '@/services/salesOrderTimesheet.service';
import PrintableSalesOrderTimesheet from '@/components/sales/PrintableSalesOrderTimesheet';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { formatMoney } from '@/utils/format';
import { useSystemSettings } from '@/hooks/useSettings';

const { Title, Text } = Typography;

const ATTENDANCE_OPTIONS = [
  { value: 'W', label: 'W — Worked' },
  { value: 'T', label: 'T — Travel' },
  { value: 'O', label: 'O — Off' },
  { value: 'H', label: 'H — Holiday' },
  { value: 'S', label: 'S — Sick' },
  { value: 'L', label: 'L — Leave' },
  { value: 'X', label: 'X — Not deployed' },
];

function periodLabel(year: number, month: number) {
  return `${year}-${String(month).padStart(2, '0')}`;
}

function daysInMonth(year: number, month: number) {
  return new Date(year, month, 0).getDate();
}

function workDateKey(year: number, month: number, day: number) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function draftFromPerson(person: SalesOrderTimesheetPerson): Record<string, string | undefined> {
  const next: Record<string, string | undefined> = {};
  for (const d of person.days || []) {
    next[String(d.work_date).slice(0, 10)] = d.attendance_code || undefined;
  }
  return next;
}

const statusColor: Record<string, string> = {
  Draft: 'default',
  Submitted: 'processing',
  Approved: 'success',
  Rejected: 'error',
  Invoiced: 'purple',
};

const SalesOrderTimesheetPage: React.FC = () => {
  const { id: salesOrderId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const timesheetIdParam = searchParams.get('timesheetId');
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const { data: settings } = useSystemSettings();
  const [selectedId, setSelectedId] = useState<string | null>(timesheetIdParam);
  const [newYear, setNewYear] = useState(new Date().getFullYear());
  const [newMonth, setNewMonth] = useState(new Date().getMonth() + 1);
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [attendancePerson, setAttendancePerson] = useState<SalesOrderTimesheetPerson | null>(null);
  const [attendanceDraft, setAttendanceDraft] = useState<Record<string, string | undefined>>({});
  const [attendanceDirty, setAttendanceDirty] = useState(false);
  const [addForm] = Form.useForm();

  const openAttendance = (person: SalesOrderTimesheetPerson) => {
    setAttendancePerson(person);
    setAttendanceDraft(draftFromPerson(person));
    setAttendanceDirty(false);
  };

  const closeAttendance = () => {
    setAttendancePerson(null);
    setAttendanceDraft({});
    setAttendanceDirty(false);
  };

  const { data: order, isLoading: orderLoading } = useQuery({
    queryKey: ['sales-order', salesOrderId],
    queryFn: () => salesOrderService.getById(salesOrderId!),
    enabled: !!salesOrderId,
  });

  const { data: list = [], isLoading: listLoading } = useQuery({
    queryKey: ['so-timesheets', salesOrderId],
    queryFn: () => salesOrderTimesheetService.listBySalesOrder(salesOrderId!),
    enabled: !!salesOrderId,
  });

  const activeId = selectedId || list[0]?.id || null;

  const { data: timesheet, isLoading: detailLoading } = useQuery({
    queryKey: ['so-timesheet', activeId],
    queryFn: () => salesOrderTimesheetService.getById(activeId!),
    enabled: !!activeId,
  });

  const { data: preview } = useQuery({
    queryKey: ['so-timesheet-preview', activeId],
    queryFn: () => salesOrderTimesheetService.billingPreview(activeId!),
    enabled: !!activeId,
  });

  const soLineOptions = useMemo(
    () =>
      (order?.items || []).map((item) => ({
        value: item.id!,
        label: `${item.line_number}. ${item.item_description}`,
      })),
    [order?.items]
  );

  const soClosed =
    order?.order_status === 'Completed' || order?.order_status === 'Cancelled';

  const editable =
    !!timesheet &&
    !soClosed &&
    timesheet.status !== 'Approved' &&
    timesheet.status !== 'Invoiced';

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['so-timesheets', salesOrderId] });
    queryClient.invalidateQueries({ queryKey: ['so-timesheet', activeId] });
    queryClient.invalidateQueries({ queryKey: ['so-timesheet-preview', activeId] });
  };

  const createMutation = useMutation({
    mutationFn: () =>
      salesOrderTimesheetService.create(salesOrderId!, {
        period_year: newYear,
        period_month: newMonth,
        copy_people_from_prior_month: true,
      }),
    onSuccess: (row) => {
      message.success('Blank month created — add people and attendance manually');
      setSelectedId(row.id);
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Create failed'),
  });

  const importMutation = useMutation({
    mutationFn: (file: File) =>
      salesOrderTimesheetService.importExcel(salesOrderId!, file, {
        period_year: newYear,
        period_month: newMonth,
      }),
    onSuccess: (row) => {
      message.success('Excel imported');
      setSelectedId(row.id);
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Import failed'),
  });

  const templateMutation = useMutation({
    mutationFn: () =>
      salesOrderTimesheetService.downloadImportTemplate({
        period_year: newYear,
        period_month: newMonth,
      }),
    onSuccess: () => message.success('Template downloaded'),
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Template download failed'),
  });

  const resolveMutation = useMutation({
    mutationFn: () => salesOrderTimesheetService.resolveMatches(activeId!),
    onSuccess: () => {
      message.success('Positions re-matched');
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Match failed'),
  });

  const mapMutation = useMutation({
    mutationFn: ({ personId, soItemId }: { personId: string; soItemId: string | null }) =>
      salesOrderTimesheetService.mapPerson(activeId!, personId, soItemId),
    onSuccess: () => invalidate(),
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Map failed'),
  });

  const addPersonMutation = useMutation({
    mutationFn: (body: {
      person_name: string;
      position: string;
      badge_id?: string;
      sales_order_item_id?: string | null;
    }) => salesOrderTimesheetService.addPerson(activeId!, body),
    onSuccess: () => {
      message.success('Person added');
      setAddPersonOpen(false);
      addForm.resetFields();
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Add failed'),
  });

  const removePersonMutation = useMutation({
    mutationFn: (personId: string) => salesOrderTimesheetService.removePerson(activeId!, personId),
    onSuccess: () => {
      message.success('Person removed');
      if (attendancePerson) closeAttendance();
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Remove failed'),
  });

  const saveAttendanceMutation = useMutation({
    mutationFn: () => {
      if (!attendancePerson || !activeId || !timesheet) {
        return Promise.reject(new Error('No attendance person selected'));
      }
      const total = daysInMonth(timesheet.period_year, timesheet.period_month);
      const days = Array.from({ length: total }, (_, i) => {
        const work_date = workDateKey(timesheet.period_year, timesheet.period_month, i + 1);
        return {
          work_date,
          attendance_code: attendanceDraft[work_date] || null,
        };
      });
      return salesOrderTimesheetService.savePersonDays(activeId, attendancePerson.id, days, {
        replace_all: true,
      });
    },
    onSuccess: (row) => {
      message.success('Attendance saved');
      invalidate();
      const updated = (row.people || []).find((p) => p.id === attendancePerson?.id);
      if (updated) {
        setAttendancePerson(updated);
        setAttendanceDraft(draftFromPerson(updated));
      }
      setAttendanceDirty(false);
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Save failed'),
  });

  const submitMutation = useMutation({
    mutationFn: () => salesOrderTimesheetService.submit(activeId!),
    onSuccess: () => {
      message.success('Submitted');
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Submit failed'),
  });

  const approveMutation = useMutation({
    mutationFn: () => salesOrderTimesheetService.approve(activeId!),
    onSuccess: () => {
      message.success('Approved');
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Approve failed'),
  });

  const invoiceMutation = useMutation({
    mutationFn: () => salesOrderTimesheetService.createInvoice(activeId!),
    onSuccess: (inv: any) => {
      message.success('Invoice created');
      invalidate();
      if (inv?.id) navigate(`/growth/invoices/${inv.id}`);
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Invoice failed'),
  });

  const attendanceDays = useMemo(() => {
    if (!timesheet || !attendancePerson) return [];
    const total = daysInMonth(timesheet.period_year, timesheet.period_month);
    return Array.from({ length: total }, (_, i) => {
      const day = i + 1;
      const key = workDateKey(timesheet.period_year, timesheet.period_month, day);
      return { day, work_date: key, code: attendanceDraft[key] || undefined };
    });
  }, [timesheet, attendancePerson, attendanceDraft]);

  if (orderLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!order) {
    return <Text type="danger">Customer order not found</Text>;
  }

  if (order.fulfillment_mode !== 'timesheet') {
    return (
      <Card>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/growth/orders/${salesOrderId}`)}>
          Back
        </Button>
        <Title level={4} style={{ marginTop: 16 }}>
          Timesheet billing is not enabled for this customer order
        </Title>
        <Text type="secondary">fulfillment_mode must be timesheet.</Text>
      </Card>
    );
  }

  const unmatched = (timesheet?.people || []).filter((p) => !p.sales_order_item_id).length;

  const handlePrintTimesheet = () => {
    if (!order || !timesheet) return;
    const companySettings = settings?.filter((s: any) => s.category === 'company') || [];
    const getSetting = (key: string) =>
      companySettings.find((s: any) => s.setting_key === key)?.setting_value ?? '';
    const companyInfo = {
      name: getSetting('company_name') || 'Company Name',
      address: getSetting('company_address') || '',
      phone: getSetting('company_phone') || '',
      email: getSetting('company_email') || '',
      taxNumber: getSetting('tax_registration_number')
        ? `TRN: ${getSetting('tax_registration_number')}`
        : '',
    };
    const mainContent = document.getElementById('root');
    if (mainContent) mainContent.style.display = 'none';
    const printContainer = document.createElement('div');
    printContainer.id = 'print-container';
    printContainer.style.display = 'block';
    document.body.appendChild(printContainer);
    const root = ReactDOM.createRoot(printContainer);
    const cleanup = () => {
      root.unmount();
      if (document.body.contains(printContainer)) document.body.removeChild(printContainer);
      if (mainContent) mainContent.style.display = 'block';
      window.removeEventListener('afterprint', cleanup);
    };
    root.render(
      <PrintableSalesOrderTimesheet
        order={order}
        timesheet={timesheet}
        preview={preview}
        companyInfo={companyInfo}
        onClose={cleanup}
      />
    );
    window.addEventListener('afterprint', cleanup);
  };

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/growth/orders/${salesOrderId}`)}>
          Back to SO
        </Button>
        <Title level={4} style={{ margin: 0 }}>
          Timesheets — {order.sales_order_number}
        </Title>
        {soClosed && (
          <Tag color={order.order_status === 'Completed' ? 'purple' : 'red'}>
            Customer order {order.order_status}
          </Tag>
        )}
        {timesheet && (
          <Button icon={<PrinterOutlined />} onClick={handlePrintTimesheet}>
            Print timesheet
          </Button>
        )}
        {timesheet && (
          <ReportDocumentActions
            reportKey="sales-order-timesheet"
            params={{ id: timesheet.id }}
            documentId={timesheet.id}
            sendPermissions={['growth.order_timesheet.edit', 'growth.order.edit', 'growth.view']}
            emailLabel="Email customer"
          />
        )}
      </Space>

      <Row gutter={16}>
        <Col xs={24} lg={8}>
          <Card title="Periods" size="small" loading={listLoading}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Table
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={list}
                onRow={(row) => ({
                  onClick: () => setSelectedId(row.id),
                  style: {
                    cursor: 'pointer',
                    background: row.id === activeId ? 'rgba(22,119,255,0.08)' : undefined,
                  },
                })}
                columns={[
                  {
                    title: 'Period',
                    render: (_: unknown, row: SalesOrderTimesheet) =>
                      periodLabel(row.period_year, row.period_month),
                  },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    render: (s: string) => <Tag color={statusColor[s] || 'default'}>{s}</Tag>,
                  },
                ]}
              />
              <Space wrap>
                <InputNumber
                  min={2000}
                  max={2100}
                  value={newYear}
                  onChange={(v) => setNewYear(Number(v) || newYear)}
                />
                <InputNumber
                  min={1}
                  max={12}
                  value={newMonth}
                  onChange={(v) => setNewMonth(Number(v) || newMonth)}
                />
              </Space>
              <Button
                type="primary"
                icon={<FormOutlined />}
                loading={createMutation.isPending}
                disabled={soClosed}
                onClick={() => createMutation.mutate()}
                block
              >
                Enter manually (new month)
              </Button>
              <Button
                icon={<DownloadOutlined />}
                loading={templateMutation.isPending}
                onClick={() => templateMutation.mutate()}
                block
              >
                Download import template
              </Button>
              <Upload
                accept=".xlsx,.xls"
                showUploadList={false}
                disabled={soClosed}
                beforeUpload={(file) => {
                  if (soClosed) return false;
                  importMutation.mutate(file);
                  return false;
                }}
              >
                <Button
                  icon={<FileExcelOutlined />}
                  loading={importMutation.isPending}
                  disabled={soClosed}
                  block
                >
                  Import Excel
                </Button>
              </Upload>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Template and import use attendance codes W/T/O/H/S/L/X. Billing uses W and T only.
              </Text>
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          {!activeId ? (
            <Card>
              <Text type="secondary">
                Create a month to enter attendance manually, or download the template and import Excel.
              </Text>
            </Card>
          ) : (
            <Spin spinning={detailLoading}>
              <Card
                title={
                  timesheet
                    ? `Period ${periodLabel(timesheet.period_year, timesheet.period_month)}`
                    : 'Timesheet'
                }
                extra={timesheet ? <Tag color={statusColor[timesheet.status]}>{timesheet.status}</Tag> : null}
              >
                <Space wrap style={{ marginBottom: 16 }}>
                  <Button
                    icon={<PlusOutlined />}
                    disabled={!editable}
                    onClick={() => {
                      addForm.resetFields();
                      setAddPersonOpen(true);
                    }}
                  >
                    Add person
                  </Button>
                  <Button
                    icon={<ThunderboltOutlined />}
                    onClick={() => resolveMutation.mutate()}
                    loading={resolveMutation.isPending}
                    disabled={!editable}
                  >
                    Auto-match positions
                  </Button>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    loading={submitMutation.isPending}
                    disabled={!timesheet || (timesheet.status !== 'Draft' && timesheet.status !== 'Rejected')}
                    onClick={() => submitMutation.mutate()}
                  >
                    Submit
                  </Button>
                  <Button
                    icon={<CheckCircleOutlined />}
                    loading={approveMutation.isPending}
                    disabled={!timesheet || (timesheet.status !== 'Submitted' && timesheet.status !== 'Draft')}
                    onClick={() => approveMutation.mutate()}
                  >
                    Approve
                  </Button>
                  <Button
                    type="primary"
                    icon={<FileTextOutlined />}
                    loading={invoiceMutation.isPending}
                    disabled={soClosed || !timesheet || timesheet.status !== 'Approved'}
                    onClick={() =>
                      modal.confirm({
                        title: 'Create tax invoice from this timesheet?',
                        content:
                          'Invoice quantity = people with W/T attendance on each position; unit price uses calendar prorata on the monthly rate.',
                        onOk: () => invoiceMutation.mutateAsync(),
                      })
                    }
                  >
                    Create invoice
                  </Button>
                </Space>

                {unmatched > 0 && (
                  <Text type="warning" style={{ display: 'block', marginBottom: 12 }}>
                    {unmatched} person row(s) need a customer order line mapping before submit.
                  </Text>
                )}

                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  scroll={{ x: true }}
                  dataSource={timesheet?.people || []}
                  columns={[
                    { title: '#', dataIndex: 'line_number', width: 50 },
                    { title: 'Name', dataIndex: 'person_name', width: 160 },
                    { title: 'Position', dataIndex: 'position', width: 200 },
                    {
                      title: 'SO line',
                      width: 260,
                      render: (_: unknown, row) => (
                        <Select
                          allowClear
                          style={{ width: '100%' }}
                          options={soLineOptions}
                          value={row.sales_order_item_id || undefined}
                          disabled={!editable}
                          onChange={(v) =>
                            mapMutation.mutate({ personId: row.id, soItemId: v ?? null })
                          }
                          placeholder="Map to SO line"
                        />
                      ),
                    },
                    {
                      title: 'W/T days',
                      width: 80,
                      render: (_: unknown, row) =>
                        (row.days || []).filter((d) =>
                          ['W', 'T'].includes(String(d.attendance_code).toUpperCase())
                        ).length,
                    },
                    {
                      title: '',
                      width: 180,
                      render: (_: unknown, row) => (
                        <Space>
                          <Button
                            size="small"
                            icon={<EditOutlined />}
                            disabled={!editable}
                            onClick={() => openAttendance(row)}
                          >
                            Attendance
                          </Button>
                          <Popconfirm
                            title="Remove this person?"
                            disabled={!editable}
                            onConfirm={() => removePersonMutation.mutate(row.id)}
                          >
                            <Button
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              disabled={!editable}
                              loading={removePersonMutation.isPending}
                            />
                          </Popconfirm>
                        </Space>
                      ),
                    },
                  ]}
                />

                <DividerLike />

                <Title level={5}>Billing preview</Title>
                <Table
                  size="small"
                  rowKey="sales_order_item_id"
                  pagination={false}
                  dataSource={preview?.lines || []}
                  columns={[
                    { title: 'Line', dataIndex: 'line_number', width: 60 },
                    { title: 'Description', dataIndex: 'item_description' },
                    {
                      title: 'Attendance',
                      dataIndex: 'attendance_exists',
                      render: (v: boolean) => (v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>),
                    },
                    { title: 'People', dataIndex: 'people_count', width: 70 },
                    {
                      title: 'Inv qty',
                      dataIndex: 'proposed_quantity',
                      width: 70,
                    },
                    { title: 'Avg days', dataIndex: 'avg_billable_days', width: 90 },
                    {
                      title: 'Prorata',
                      dataIndex: 'prorata',
                      width: 90,
                      render: (v: number) => `${Math.round(v * 10000) / 100}%`,
                    },
                    {
                      title: 'Unit',
                      dataIndex: 'proposed_unit_price',
                      width: 110,
                      render: (v: number) => formatMoney(v, { currency: order.currency || 'AED' }),
                    },
                    {
                      title: 'Amount',
                      dataIndex: 'proposed_line_amount',
                      width: 120,
                      render: (v: number) => formatMoney(v, { currency: order.currency || 'AED' }),
                    },
                  ]}
                />
                <Descriptions size="small" style={{ marginTop: 12 }} column={2}>
                  <Descriptions.Item label="Calendar days">
                    {preview?.calendar_days ?? '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Billable SO lines">
                    {preview?.billable_line_count ?? 0}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Spin>
          )}
        </Col>
      </Row>

      <Modal
        title="Add person (manual)"
        open={addPersonOpen}
        onCancel={() => setAddPersonOpen(false)}
        onOk={() => addForm.submit()}
        confirmLoading={addPersonMutation.isPending}
        okText="Add"
        destroyOnClose
      >
        <Form
          form={addForm}
          layout="vertical"
          onFinish={(values) =>
            addPersonMutation.mutate({
              person_name: values.person_name,
              position: values.position,
              badge_id: values.badge_id,
              sales_order_item_id: values.sales_order_item_id || null,
            })
          }
        >
          <Form.Item
            name="person_name"
            label="Name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="position"
            label="Position"
            rules={[{ required: true, message: 'Position is required' }]}
            extra="Should match a customer order line description when possible"
          >
            <Input />
          </Form.Item>
          <Form.Item name="badge_id" label="Badge / ID">
            <Input />
          </Form.Item>
          <Form.Item name="sales_order_item_id" label="Customer order line">
            <Select allowClear options={soLineOptions} placeholder="Optional — auto-match by position" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          attendancePerson
            ? `Attendance — ${attendancePerson.person_name}`
            : 'Attendance'
        }
        open={!!attendancePerson}
        onCancel={closeAttendance}
        width={720}
        destroyOnClose
        footer={
          <Space>
            <Button onClick={closeAttendance}>Cancel</Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              disabled={!editable || !attendanceDirty}
              loading={saveAttendanceMutation.isPending}
              onClick={() => saveAttendanceMutation.mutate()}
            >
              Save
            </Button>
          </Space>
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Set codes for each calendar day, then click Save. W and T count toward billing prorata.
          {attendanceDirty ? ' (unsaved changes)' : ''}
        </Text>
        <Table
          size="small"
          rowKey="work_date"
          pagination={false}
          dataSource={attendanceDays}
          scroll={{ y: 420 }}
          columns={[
            { title: 'Day', dataIndex: 'day', width: 70 },
            { title: 'Date', dataIndex: 'work_date', width: 120 },
            {
              title: 'Code',
              render: (_: unknown, row: { work_date: string; code?: string }) => (
                <Select
                  allowClear
                  style={{ width: 200 }}
                  options={ATTENDANCE_OPTIONS}
                  value={row.code}
                  disabled={!editable || !attendancePerson}
                  onChange={(code) => {
                    setAttendanceDraft((prev) => {
                      const next = { ...prev };
                      if (!code) delete next[row.work_date];
                      else next[row.work_date] = code;
                      return next;
                    });
                    setAttendanceDirty(true);
                  }}
                  placeholder="Select"
                />
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
};

function DividerLike() {
  return <div style={{ height: 1, background: 'rgba(0,0,0,0.06)', margin: '20px 0' }} />;
}

export default SalesOrderTimesheetPage;
